import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Phone, ShieldCheck, Hammer, Compass, HeartHandshake, Star } from "lucide-react";
import BlueprintIntro from "@/components/BlueprintIntro";
import Reveal from "@/components/Reveal";
import { HERO_PHOTO, FEATURED_PHOTOS, HOME_INTRO_PHOTO, CRAFTSMANSHIP_PHOTO, photoDims } from "@/lib/photos";
import { LOGO_PRIMARY } from "@/lib/brand";
import { SERVICES } from "@/lib/services";
import { track } from "@/lib/analytics";
import FaqSection from "@/components/FaqSection";
import SEOHead from "@/components/SEOHead";
import { HOME_FAQS } from "@/lib/faqs";

// Alt text for the featured-work strip (each maps to FEATURED_PHOTOS by index)
const FEATURED_ALTS = [
 "Custom-built home exterior in Tahlequah, OK by Harris Homes",
 "Open-plan living room with wood beams by Harris Homes, Tahlequah",
 "Custom kitchen built by Harris Homes in Tahlequah, OK",
 "Bathroom with custom finishes by Harris Homes, Tahlequah OK",
 "Built-in carpentry detail by Harris Homes, Tahlequah",
 "Finished home exterior by Harris Homes, Tahlequah OK",
];

// Real 5-star Google reviews (provided by the owner, Oct 2026)
const REVIEWS = [
 {
 name: "Andrea Young",
 meta: "Custom home · Grandview Ridge, Tahlequah",
 quote:
 "Harris Homes built me a beautiful home in Grandview Ridge in Tahlequah and I couldn't be happier! Quality workmanship throughout the house and Kolin was great to work with.",
 },
 {
 name: "Holly Akins",
 meta: "Custom home · Google review",
 quote:
 "Kolin made the entire process as smooth as possible. He kept us informed throughout the project, and the craftsmanship and quality of our home exceeded our expectations.",
 },
 {
 name: "Charlie Wu",
 meta: "Custom home · Google review",
 quote:
 "I bought the very first home Harris Homes LLC built — the quality was leagues above other new builds on the market. No corners were cut.",
 },
];

export default function Home() {
 return (
 <div data-testid="home-page">
 <SEOHead
 title="Harris Homes | Custom Home Builder in Tahlequah, OK"
 description="Harris Homes builds custom homes, shop houses and additions in Tahlequah, OK. See our finished work and call 918-373-4341 for a free quote."
 canonical="https://harrishomesok.com/"
 />
 {/* ----------- HERO, Brand-led cover ----------- */}
 <section
 data-testid="hero-section"
 className="relative -mt-[96px] md:-mt-[128px] h-screen min-h-[680px] max-h-[1100px] flex items-center justify-center overflow-hidden"
 >
 {/* Full-bleed hero photo with cinematic vignette */}
 <BlueprintIntro imageUrl={HERO_PHOTO} alt="Custom-built home in Tahlequah, OK by Harris Homes" />

 {/* Centered brand lockup */}
 <div className="relative z-10 w-full max-w-[1100px] px-6 md:px-10 flex flex-col items-center text-center">
 {/* SEO H1 - visually hidden to preserve the existing hero art, still crawlable */}
 <h1 className="sr-only">Custom Home Builder in Tahlequah, Oklahoma</h1>
 {/* Logo mark */}
 <motion.img
 src={LOGO_PRIMARY}
 alt="Harris Homes LLC"
 width={600}
 height={275}
 initial={{ opacity: 0, y: 14, scale: 0.98 }}
 animate={{ opacity: 1, y: 0, scale: 1 }}
 transition={{ delay: 1.2, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
 className="w-[280px] sm:w-[380px] md:w-[480px] h-auto"
 style={{ filter: "drop-shadow(0 6px 40px rgba(0,0,0,0.6))" }}
 />

 {/* Gold rule */}
 <motion.div
 initial={{ scaleX: 0 }}
 animate={{ scaleX: 1 }}
 transition={{ delay: 1.45, duration: 0.7 }}
 className="h-px w-28 md:w-40 mt-8 mb-7 origin-center"
 style={{ background: "var(--hh-gold)" }}
 />

 {/* Tagline */}
 <motion.p
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 1.55, duration: 0.8 }}
 className="font-display italic text-2xl sm:text-3xl md:text-4xl leading-tight max-w-2xl"
 style={{ color: "var(--hh-text)" }}
 >
 Custom homes, remodels &amp; additions
 <br className="hidden sm:block" /> built by hand, made to last.
 </motion.p>

 {/* CTAs */}
 <motion.div
 initial={{ opacity: 0, y: 14 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 1.75, duration: 0.8 }}
 className="mt-12 md:mt-14 flex flex-col sm:flex-row items-center gap-5"
 >
 <Link to="/contact" data-testid="hero-cta-start" onClick={() => track.ctaClick("Start Your Project", "hero")} className="btn-gold">
 Start Your Project <ArrowRight size={14} strokeWidth={1.6} />
 </Link>
 <Link
 to="/projects"
 data-testid="hero-cta-work"
 onClick={() => track.ctaClick("View Our Work", "hero")}
 className="text-[12px] tracking-[0.32em] uppercase flex items-center gap-2 hover:text-[var(--hh-gold-light)] transition-colors"
 style={{ color: "var(--hh-text)", fontFamily: "Marcellus, serif" }}
 >
 View Our Work <ArrowUpRight size={12} />
 </Link>
 </motion.div>
 </div>

 {/* Bottom scroll cue */}
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 2.0, duration: 0.6 }}
 className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
 >
 <span
 className="text-[9px] tracking-[0.5em] uppercase"
 style={{ color: "var(--hh-text-dim)", fontFamily: "Marcellus, serif" }}
 >
 Scroll
 </span>
 <motion.div
 animate={{ scaleY: [0.4, 1, 0.4], opacity: [0.3, 1, 0.3] }}
 transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
 className="w-px h-10 origin-top"
 style={{ background: "var(--hh-gold)" }}
 />
 </motion.div>
 </section>

 {/* ----------- INTRO / ABOUT STRIP ----------- */}
 <section data-testid="intro-section" className="py-24 md:py-36 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-center">
 <Reveal className="md:col-span-7">
 <div className="overline mb-6">A Word From Harris Homes</div>
 <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl">
 A home isn't a product.
 <br />
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 It's where your life happens.
 </span>
 </h2>
 <div className="gold-rule w-24 my-8" />
 <p className="text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--hh-text-dim)" }}>
 We build for the families who plan to stay. As a{" "}
 <Link
 to="/custom-home-builder-tahlequah-ok"
 data-testid="intro-tahlequah-link"
 className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]"
 style={{ color: "var(--hh-gold)" }}
 >
 custom home builder in Tahlequah, Oklahoma
 </Link>
 , every project is led personally, start to finish, with honest
 budgets, careful scheduling, and craftsmanship that shows up in
 every reveal, corner, and finish.
 </p>
 <p className="mt-6 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--hh-text-dim)" }}>
 We build custom homes,{" "}
 <Link to="/shop-house-barndominium-builder-tahlequah-ok" data-testid="intro-shophouse-link" className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]" style={{ color: "var(--hh-gold)" }}>
 shop houses and barndominiums
 </Link>
 ,{" "}
 <Link to="/home-additions-tahlequah-ok" data-testid="intro-additions-link" className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]" style={{ color: "var(--hh-gold)" }}>
 additions
 </Link>
 , and{" "}
 <Link to="/home-remodeling-tahlequah-ok" data-testid="intro-remodeling-link" className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]" style={{ color: "var(--hh-gold)" }}>
 remodels
 </Link>
 {" "}— explore our{" "}
 <Link to="/services" data-testid="intro-services-link" className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]" style={{ color: "var(--hh-gold)" }}>
 services
 </Link>
 {" "}or browse the{" "}
 <Link to="/projects" data-testid="intro-projects-link" className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]" style={{ color: "var(--hh-gold)" }}>
 finished work
 </Link>
 .
 </p>
 <div className="mt-10">
 <Link to="/about" data-testid="intro-about-link" className="btn-ghost">
 Our Approach <ArrowRight size={14} />
 </Link>
 </div>
 </Reveal>
 <Reveal delay={0.15} className="md:col-span-5">
 <div className="relative img-zoom">
 <img
 src={HOME_INTRO_PHOTO}
 alt="Residential project management and custom home construction by Harris Homes Oklahoma builder"
 width={photoDims(HOME_INTRO_PHOTO)?.w}
 height={photoDims(HOME_INTRO_PHOTO)?.h}
 className="w-full h-[480px] object-cover"
 loading="lazy"
 />
 <div
 className="absolute -bottom-6 -left-6 hidden md:block px-6 py-4 border"
 style={{
 background: "var(--hh-bg)",
 borderColor: "var(--hh-gold)",
 }}
 >
 <div className="overline">Where Life Happens</div>
 <div className="font-display text-2xl mt-1">Built In Tahlequah, OK</div>
 </div>
 </div>
 </Reveal>
 </div>
 </section>

 {/* ----------- SERVICES OVERVIEW ----------- */}
 <section
 data-testid="services-overview"
 className="py-24 md:py-32 px-6 md:px-10"
 style={{ background: "var(--hh-bg-2)" }}
 >
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
 <div>
 <div className="overline mb-5">What We Build</div>
 <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl">
 Custom-built. Honestly priced.
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 {" "}Personally delivered.
 </span>
 </h2>
 </div>
 <Link to="/services" data-testid="services-overview-cta" className="btn-ghost self-start">
 All Services <ArrowRight size={14} />
 </Link>
 </div>
 </Reveal>
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--hh-border)" }}>
 {SERVICES.map((s, i) => (
 <Reveal key={s.id} delay={i * 0.06}>
 <div
 data-testid={`service-card-${s.id}`}
 className="h-full p-8 md:p-10 transition-colors duration-500 group hover:bg-[var(--hh-bg-3)]"
 style={{ background: "var(--hh-bg-2)" }}
 >
 <s.Icon
 size={28}
 strokeWidth={1.2}
 style={{ color: "var(--hh-gold)" }}
 />
 <h3 className="font-display text-2xl md:text-3xl mt-8 mb-3 leading-tight">
 {s.title}
 </h3>
 <p
 className="text-sm leading-relaxed"
 style={{ color: "var(--hh-text-dim)" }}
 >
 {s.short}
 </p>
 <div
 className="mt-10 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase opacity-0 group-hover:opacity-100 transition-opacity"
 style={{ color: "var(--hh-gold)", fontFamily: "Marcellus, serif" }}
 >
 Learn More <ArrowRight size={12} />
 </div>
 </div>
 </Reveal>
 ))}
 </div>
 </div>
 </section>

 {/* ----------- FEATURED WORK ----------- */}
 <section data-testid="featured-work" className="py-24 md:py-32 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
 <div>
 <div className="overline mb-5">Recent Work</div>
 <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl">
 Built with our own hands.
 </h2>
 </div>
 <Link to="/projects" data-testid="featured-work-cta" className="btn-ghost self-start">
 See All Projects <ArrowRight size={14} />
 </Link>
 </div>
 </Reveal>

 <div className="grid grid-cols-12 gap-3 md:gap-4">
 {/* Asymmetric premium layout */}
 <Reveal className="col-span-12 md:col-span-8 row-span-2">
 <div className="relative img-zoom h-full min-h-[420px]">
 <img
 src={FEATURED_PHOTOS[0]}
 alt={FEATURED_ALTS[0]}
 width={photoDims(FEATURED_PHOTOS[0])?.w}
 height={photoDims(FEATURED_PHOTOS[0])?.h}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 <Reveal delay={0.1} className="col-span-6 md:col-span-4">
 <div className="relative img-zoom h-[200px] md:h-[210px]">
 <img
 src={FEATURED_PHOTOS[1]}
 alt={FEATURED_ALTS[1]}
 width={photoDims(FEATURED_PHOTOS[1])?.w}
 height={photoDims(FEATURED_PHOTOS[1])?.h}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 <Reveal delay={0.15} className="col-span-6 md:col-span-4">
 <div className="relative img-zoom h-[200px] md:h-[210px]">
 <img
 src={FEATURED_PHOTOS[2]}
 alt={FEATURED_ALTS[2]}
 width={photoDims(FEATURED_PHOTOS[2])?.w}
 height={photoDims(FEATURED_PHOTOS[2])?.h}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 <Reveal delay={0.2} className="col-span-6 md:col-span-4">
 <div className="relative img-zoom h-[220px]">
 <img
 src={FEATURED_PHOTOS[3]}
 alt={FEATURED_ALTS[3]}
 width={photoDims(FEATURED_PHOTOS[3])?.w}
 height={photoDims(FEATURED_PHOTOS[3])?.h}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 <Reveal delay={0.25} className="col-span-6 md:col-span-4">
 <div className="relative img-zoom h-[220px]">
 <img
 src={FEATURED_PHOTOS[4]}
 alt={FEATURED_ALTS[4]}
 width={photoDims(FEATURED_PHOTOS[4])?.w}
 height={photoDims(FEATURED_PHOTOS[4])?.h}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 <Reveal delay={0.3} className="col-span-12 md:col-span-4">
 <div className="relative img-zoom h-[220px]">
 <img
 src={FEATURED_PHOTOS[5]}
 alt={FEATURED_ALTS[5]}
 width={photoDims(FEATURED_PHOTOS[5])?.w}
 height={photoDims(FEATURED_PHOTOS[5])?.h}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 </div>
 </div>
 </section>

 {/* ----------- WHY HARRIS HOMES ----------- */}
 <section
 data-testid="why-us"
 className="py-24 md:py-32 px-6 md:px-10 relative overflow-hidden"
 style={{ background: "var(--hh-bg-2)" }}
 >
 <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 items-center">
 <Reveal className="md:col-span-5">
 <div className="relative img-zoom">
 <img
 src={CRAFTSMANSHIP_PHOTO}
 alt="Custom carpentry and built-in wood finishes completed by Harris Homes in Tahlequah Oklahoma"
 width={photoDims(CRAFTSMANSHIP_PHOTO)?.w}
 height={photoDims(CRAFTSMANSHIP_PHOTO)?.h}
 className="w-full h-[560px] object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 <div className="md:col-span-7">
 <Reveal>
 <div className="overline mb-5">Why Harris Homes</div>
 <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl">
 Built by hand,
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 {" "}backed by a name.
 </span>
 </h2>
 </Reveal>
 <div className="mt-10 grid sm:grid-cols-2 gap-8 md:gap-10">
 {[
 {
 Icon: ShieldCheck,
 title: "Trusted & Accountable",
 body: "One builder, one point of contact, one promise, from the first conversation to the final walkthrough.",
 },
 {
 Icon: Hammer,
 title: "Real Craftsmanship",
 body: "We treat trim, transitions, and finishes as the whole job, because that's what you live with every day.",
 },
 {
 Icon: Compass,
 title: "Local & Rooted",
 body: "Born and built in Tahlequah, OK, working with people and trades we know and stand behind.",
 },
 {
 Icon: HeartHandshake,
 title: "Honest Process",
 body: "Clear budgets, real timelines, and straightforward communication so you always know where things stand.",
 },
 ].map((p, i) => (
 <Reveal key={p.title} delay={i * 0.08}>
 <div
 className="flex gap-5 items-start"
 data-testid={`why-pillar-${i}`}
 >
 <p.Icon
 size={28}
 strokeWidth={1.2}
 style={{ color: "var(--hh-gold)" }}
 />
 <div>
 <h3 className="font-display text-2xl mb-2 leading-tight">
 {p.title}
 </h3>
 <p
 className="text-sm leading-relaxed"
 style={{ color: "var(--hh-text-dim)" }}
 >
 {p.body}
 </p>
 </div>
 </div>
 </Reveal>
 ))}
 </div>
 </div>
 </div>
 </section>

 {/* ----------- GOOGLE REVIEWS ----------- */}
 <section data-testid="reviews-strip" className="py-24 md:py-32 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div className="text-center mb-14">
 <div className="overline mb-5">Five Stars on Google</div>
 <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
 What homeowners say about
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 {" "}building with Kolin.
 </span>
 </h2>
 </div>
 </Reveal>
 <div className="grid md:grid-cols-3 gap-px" style={{ background: "var(--hh-border)" }}>
 {REVIEWS.map((r, i) => (
 <Reveal key={r.name} delay={i * 0.08}>
 <figure
 data-testid={`review-${i}`}
 className="h-full p-8 md:p-10 flex flex-col"
 style={{ background: "var(--hh-bg)" }}
 >
 <div className="flex gap-1 mb-6" aria-label="Rated 5 out of 5 stars">
 {[...Array(5)].map((_, s) => (
 <Star key={s} size={14} strokeWidth={0} fill="var(--hh-gold)" style={{ color: "var(--hh-gold)" }} />
 ))}
 </div>
 <blockquote className="font-display text-xl md:text-2xl leading-snug flex-1" style={{ color: "var(--hh-text)" }}>
 "{r.quote}"
 </blockquote>
 <figcaption className="mt-8">
 <div
 className="text-[11px] tracking-[0.32em] uppercase"
 style={{ color: "var(--hh-gold)", fontFamily: "Marcellus, serif" }}
 >
 {r.name}
 </div>
 <div className="text-xs mt-2" style={{ color: "var(--hh-text-dim)" }}>
 {r.meta}
 </div>
 </figcaption>
 </figure>
 </Reveal>
 ))}
 </div>
 <Reveal delay={0.2}>
 <p
 className="mt-8 text-center text-[10px] tracking-[0.32em] uppercase"
 style={{ color: "var(--hh-text-dim)", fontFamily: "Marcellus, serif" }}
 >
 From verified Google reviews · Harris Homes LLC, Tahlequah
 </p>
 </Reveal>
 </div>
 </section>

 {/* ----------- FAQ ----------- */}
 <FaqSection
 overline="Frequently Asked"
 headingLead="Answers before we"
 headingItalic="even meet."
 items={HOME_FAQS}
 testId="home-faq"
 />

 {/* ----------- CONTACT CTA ----------- */}
 <section
 data-testid="contact-cta"
 className="py-32 md:py-40 px-6 md:px-10 relative"
 >
 <div className="max-w-[1100px] mx-auto text-center">
 <Reveal>
 <div className="overline mb-6">Let's Talk</div>
 <h2 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight">
 Have a project in mind?
 <br />
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 Let's build it right.
 </span>
 </h2>
 <p className="mt-8 max-w-xl mx-auto text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
 We take on a limited number of projects each year. Reach out
 early, even if you're still in the dreaming stage.
 </p>
 <div className="mt-10 flex flex-wrap gap-4 justify-center">
 <Link to="/contact" data-testid="contact-cta-primary" onClick={() => track.ctaClick("Start a Conversation", "home-cta")} className="btn-gold">
 Start a Conversation <ArrowRight size={14} />
 </Link>
 <a href="tel:+19183734341" data-testid="contact-cta-phone" onClick={() => track.phoneClick("home-cta")} className="btn-ghost">
 <Phone size={14} strokeWidth={1.5} /> (918) 373-4341
 </a>
 </div>
 <p
 data-testid="serving-line"
 className="mt-14 text-[10px] tracking-[0.4em] uppercase"
 style={{ color: "var(--hh-text-dim)", fontFamily: "Marcellus, serif" }}
 >
 Serving Tahlequah &amp; Cherokee County — and nearby towns
 </p>
 </Reveal>
 </div>
 </section>
 </div>
 );
}
